import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, CardActions,
  Button, Chip, Divider, CircularProgress, Alert,
} from '@mui/material';
import UploadFileIcon  from '@mui/icons-material/UploadFile';
import DownloadIcon    from '@mui/icons-material/Download';
import SwapHorizIcon   from '@mui/icons-material/SwapHoriz';
import EventIcon       from '@mui/icons-material/Event';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { authGet } from '../../services/apiService';

// ── Weekend helpers ────────────────────────────────────────────────────────────

function toYMD(d) {
  return d.toISOString().slice(0, 10);
}

function fmtLabel(satStr) {
  const sat = new Date(satStr + 'T00:00:00Z');
  const sun = new Date(sat); sun.setUTCDate(sat.getUTCDate() + 1);
  const opts = { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' };
  return `${sat.toLocaleDateString('en-GB', opts)} – ${sun.toLocaleDateString('en-GB', opts)}`;
}

function fmtSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Returns { past: [...], upcoming: [...] } — each item { date, label } */
function getWeekendSlots() {
  const today = new Date();
  // normalise to UTC midnight
  const now = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
  // Saturday of current week (0=Sun … 6=Sat)
  const dayOfWeek = now.getUTCDay();
  const daysToSat = (6 - dayOfWeek + 7) % 7; // 0 if today is Sat
  const thisSat = new Date(now); thisSat.setUTCDate(now.getUTCDate() + daysToSat);

  const slots = [];
  // 2 past Saturdays
  for (let i = 2; i >= 1; i--) {
    const d = new Date(thisSat); d.setUTCDate(thisSat.getUTCDate() - i * 7);
    slots.push({ date: toYMD(d), label: fmtLabel(toYMD(d)), isPast: true });
  }
  // 5 upcoming (current Sat + next 4)
  for (let i = 0; i < 5; i++) {
    const d = new Date(thisSat); d.setUTCDate(thisSat.getUTCDate() + i * 7);
    slots.push({ date: toYMD(d), label: fmtLabel(toYMD(d)), isPast: false });
  }
  return slots;
}

// ── Main component ─────────────────────────────────────────────────────────────

const ActivitiesTab = () => {
  const slots = getWeekendSlots();
  const [records, setRecords]   = useState({});   // { weekendDate → record }
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [uploading, setUploading] = useState({});  // { weekendDate → bool }
  const [uploadErr, setUploadErr] = useState({});  // { weekendDate → string }
  const fileRefs = useRef({});

  const loadRecords = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const dates = slots.map(s => s.date).join(',');
      const res   = await authGet(`/api/weekend-activities?dates=${dates}`);
      if (res?.success) {
        const map = {};
        (res.data || []).forEach(r => { map[r.weekendDate] = r; });
        setRecords(map);
      } else {
        setError('Failed to load activities');
      }
    } catch {
      setError('Failed to load activities');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadRecords(); }, [loadRecords]);

  const handleFileSelect = (weekendDate) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    setUploading(u => ({ ...u, [weekendDate]: true }));
    setUploadErr(u => ({ ...u, [weekendDate]: null }));
    try {
      const token = localStorage.getItem('token');
      const form  = new FormData();
      form.append('file', file);
      const res = await fetch(`/api/weekend-activities/${weekendDate}/upload`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}` },
        body:    form,
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || 'Upload failed');
      setRecords(r => ({ ...r, [weekendDate]: json.data }));
    } catch (err) {
      setUploadErr(u => ({ ...u, [weekendDate]: err.message }));
    } finally {
      setUploading(u => ({ ...u, [weekendDate]: false }));
    }
  };

  const handleDownload = (weekendDate, fileName) => async () => {
    try {
      const token = localStorage.getItem('token');
      const res   = await fetch(`/api/weekend-activities/${weekendDate}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = fileName; a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };

  const past     = slots.filter(s => s.isPast);
  const upcoming = slots.filter(s => !s.isPast);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Upcoming weekends */}
      <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700, color: '#1D4434' }}>
        Upcoming Weekends
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {upcoming.map(slot => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={slot.date}>
            <WeekendCard
              slot={slot}
              record={records[slot.date] || null}
              uploading={!!uploading[slot.date]}
              uploadErr={uploadErr[slot.date]}
              fileRef={el => { fileRefs.current[slot.date] = el; }}
              onUploadClick={() => fileRefs.current[slot.date]?.click()}
              onFileSelect={handleFileSelect(slot.date)}
              onDownload={handleDownload(slot.date, records[slot.date]?.fileName)}
            />
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ mb: 3 }} />

      {/* Recent weekends */}
      <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700, color: '#1D4434' }}>
        Recent Weekends
      </Typography>
      <Grid container spacing={2}>
        {past.map(slot => (
          <Grid item xs={12} sm={6} md={4} key={slot.date}>
            <WeekendCard
              slot={slot}
              record={records[slot.date] || null}
              uploading={!!uploading[slot.date]}
              uploadErr={uploadErr[slot.date]}
              fileRef={el => { fileRefs.current[slot.date] = el; }}
              onUploadClick={() => fileRefs.current[slot.date]?.click()}
              onFileSelect={handleFileSelect(slot.date)}
              onDownload={handleDownload(slot.date, records[slot.date]?.fileName)}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// ── Weekend card ───────────────────────────────────────────────────────────────

const WeekendCard = ({ slot, record, uploading, uploadErr, fileRef, onUploadClick, onFileSelect, onDownload }) => {
  const hasUpload = !!record;

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderColor: hasUpload ? 'success.light' : 'divider',
        borderWidth: hasUpload ? 2 : 1,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2, py: 1.5,
          bgcolor: hasUpload ? 'success.50' : 'grey.50',
          borderBottom: '1px solid',
          borderColor: hasUpload ? 'success.light' : 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <EventIcon sx={{ fontSize: 16, color: hasUpload ? 'success.main' : 'text.secondary' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1, fontSize: '0.8rem' }}>
          {slot.label}
        </Typography>
        {hasUpload && (
          <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
        )}
      </Box>

      <CardContent sx={{ flex: 1, py: 1.5, px: 2 }}>
        {hasUpload ? (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, wordBreak: 'break-all' }}>
              {record.fileName}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              By {record.uploadedByName}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {fmtDate(record.uploadedAt)} · {fmtSize(record.fileSize)}
            </Typography>
            {record.overrideCount > 0 && (
              <Chip
                label={`Updated ${record.overrideCount}×`}
                size="small"
                sx={{ mt: 0.5, height: 18, fontSize: '0.65rem' }}
              />
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No activity uploaded
          </Typography>
        )}
        {uploadErr && (
          <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
            {uploadErr}
          </Typography>
        )}
      </CardContent>

      <CardActions sx={{ px: 2, pb: 1.5, pt: 0, gap: 1 }}>
        <input
          type="file"
          accept=".html,.htm"
          style={{ display: 'none' }}
          ref={fileRef}
          onChange={onFileSelect}
        />
        <Button
          size="small"
          variant={hasUpload ? 'outlined' : 'contained'}
          startIcon={uploading
            ? <CircularProgress size={12} color="inherit" />
            : hasUpload ? <SwapHorizIcon /> : <UploadFileIcon />
          }
          onClick={onUploadClick}
          disabled={uploading}
          color={hasUpload ? 'warning' : 'primary'}
          sx={{ textTransform: 'none', fontSize: '0.75rem' }}
        >
          {uploading ? 'Uploading…' : hasUpload ? 'Override' : 'Upload'}
        </Button>
        {hasUpload && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={onDownload}
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
          >
            Download
          </Button>
        )}
      </CardActions>
    </Card>
  );
};

export default ActivitiesTab;
